/**
 * @typedef {import('./Props').default} Props
 * @typedef {import('./State').default} State
 */

import { generateInstagramShareImage } from '../../utils/instagramImageGenerator';
import { translate } from '../../App/Internationalization';
import BaseButton from '../BaseButton/BaseButton';
import BaseModal from '../../App/shared/BaseModal/BaseModal';
import CopyToClipboard from '../Links/CopyToClipboard';
import images from '../../config/local/images';
import React from 'react';

/** @extends {React.Component<Props, State>}  */
export default class SocialShareModal extends React.Component {
	/**
	 * @param {Props} props
	 */
	constructor(props) {
		super(props);
		this.state = {
			imageDataUrl: null,
			loading: true,
		};
	}

	async componentDidMount() {
		if (this.props.isOpen) {
			this.generateImage();
		}
	}

	/**
	 * @param {Props} prevProps
	 */
	componentDidUpdate(prevProps) {
		if (this.props.isOpen && !prevProps.isOpen) {
			this.generateImage();
		}
	}

	async generateImage() {
		this.setState({ loading: true });
		try {
			const title = this.props.title;
			const heroImage = this.props.heroImage;
			const authorName = this.props.authorName;
			const authorImage = this.props.authorImage;
			const date = this.props.date;
			const logoUrl = images.brand.logo; // Format date if it's a timestamp/date object

			let dateString = '';
			if (date) {
				const newDate = new Date(date);
				if (!isNaN(newDate.getTime())) {
					dateString = newDate.toLocaleDateString('en-US', {
						day: 'numeric',
						month: 'long',
						year: 'numeric',
					});
				}
			}

			const byText = translate('homeowner.pages.dashboard.blog.by');
			const imageDataUrl = await generateInstagramShareImage(
				authorImage,
				authorName,
				byText,
				dateString,
				heroImage,
				logoUrl,
				title
			);
			this.setState({ imageDataUrl, loading: false });
		} catch (error) {
			this.setState({
				loading: false,
			});
		}
	}

	handleDownload = () => {
		// eslint-disable-next-line no-invalid-this
		if (this.state.imageDataUrl) {
			const link = document.createElement('a');
			link.download = `instagram-share-${Date.now()}.png`;
			// eslint-disable-next-line no-invalid-this
			link.href = this.state.imageDataUrl;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};

	getLoadingText = () => {
		// eslint-disable-next-line no-invalid-this
		if (this.state.loading) {
			return (
				<div className="flex items-center justify-center h-full p-4">
					<span className="text-xs text-gray-500">
						{'Generating Preview...'}
					</span>
				</div>
			);
			// eslint-disable-next-line no-invalid-this
		} else if (this.state.imageDataUrl) {
			return (
				<img
					alt="Instagram Preview"
					className="h-full w-auto object-contain p-1"
					// eslint-disable-next-line no-invalid-this
					src={this.state.imageDataUrl}
				/>
			);
		}
		return (
			<div className="flex items-center justify-center h-full text-red-400 p-4">
				<span className="text-xs">{'Preview Unavailable'}</span>
			</div>
		);
	};

	render() {
		if (!this.props.isOpen) {
			return null;
		}

		const onClose = this.props.handleClose;
		const url = this.props.url;

		return (
			<BaseModal onClose={onClose}>
				<div className="flex flex-col items-center text-center p-3 max-h-[90vh] overflow-y-auto w-full">
					<h2 className="text-base font-bold mb-2 text-gray-800">
						{'Share to Instagram'}
					</h2>

					<div
						className="mb-3 w-full flex justify-center bg-gray-50 rounded border border-gray-200"
						style={{ height: '260px', minHeight: '200px' }}
					>
						{this.getLoadingText()}
					</div>
					<p className="text-xs text-gray-500 mb-2 leading-tight px-1">
						{"Instagram doesn't support direct link sharing. "}
						<br className="hidden sm:inline" />
						{"We've copied the link to your clipboard!"}
					</p>
					<div className="mb-2 w-full max-w-xs">
						<CopyToClipboard
							className="w-full justify-center text-xs py-1 pointer-events-none h-8 min-h-0"
							text="Link Copied!"
							url={url}
						/>
					</div>
					<div className="flex flex-col gap-2 w-full max-w-xs">
						<BaseButton
							className="w-full justify-center bg-gradient-to-r from-purple-500 to-pink-500 text-white border-none hover:opacity-90 py-1.5 text-sm h-9 min-h-0"
							onClick={() =>
								window.open(
									'https://www.instagram.com',
									'_blank'
								)
							}
						>
							{'Open Instagram'}
						</BaseButton>

						<BaseButton
							className="w-full justify-center py-1.5 text-sm h-9 min-h-0"
							onClick={this.handleDownload}
							outline={true}
						>
							{'Download Image'}
						</BaseButton>
					</div>
				</div>
			</BaseModal>
		);
	}
}
